import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, Feature, Component, ComponentStatus, BugUrl, SubDepStatus, TestPlan, PRFeedback } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDate, formatDuration } from '../../utils/date';
import { theme } from '../../theme';
import TestPlanDetails from '../testplan/TestPlanDetails';

interface SessionDetailsProps {
  session: Session;
  onClose: () => void;
  userRole?: string;
  detailsRef?: React.RefObject<HTMLDivElement>;
  zIndex?: number;
  onSessionChange?: () => Promise<void> | void; // Callback to refresh session list
  onEdit?: (sessionId: string) => void; // Optional callback to open edit modal
  onBackToTestPlan?: () => void; // Optional callback to go back to test plan
  milestones?: Record<string, { startDate: string; endDate?: string }>;
}

const SessionDetails: React.FC<SessionDetailsProps> = ({
  session,
  onClose,
  userRole = 'user',
  detailsRef,
  zIndex = 1000,
  onSessionChange,
  onEdit,
  onBackToTestPlan,
  milestones = {}
}) => {
  const navigate = useNavigate();
  
  // Extract project code from session ID (e.g., "FS-42" → "FS")
  const projectCode = session.id?.match(/^([A-Z]+)-\d+$/)?.[1];
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [testPlan, setTestPlan] = useState<TestPlan | null>(null);
  const [showTestPlanModal, setShowTestPlanModal] = useState(false);
  const [testPlanFeatures, setTestPlanFeatures] = useState<Feature[]>([]);
  const [testPlanComponents, setTestPlanComponents] = useState<Component[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(true);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const fetchSessionDetails = async () => {
      setDetailsLoading(true);
      try {
        // Extract test plan ID from URL if present
        let testPlanId: string | null = null;
        if (session.testPlanUrl) {
          const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
          testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
        }

        // Fetch all data in parallel
        const fetchPromises: Promise<any>[] = [
          apiRequest<Feature[]>('/api/features', {}, { useAuth: true, setMessage: undefined }),
          apiRequest<Component[]>('/api/components', {}, { useAuth: true, setMessage: undefined })
        ];

        if (testPlanId) {
          fetchPromises.push(apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true, setMessage: undefined }));
        }

        const results = await Promise.all(fetchPromises);
        const [allFeaturesData, allComponentsData, testPlanData] = results;

        // Filter to only the ones in this session
        const sessionFeatures = (session.features || [])
          .map((id: string) => allFeaturesData.find((f: Feature) => f.id === id))
          .filter((f): f is Feature => f !== undefined);

        // Use components if available, fall back to subDependencies for legacy sessions
        const sessionComponentIds = (session.components && session.components.length > 0) 
          ? session.components 
          : (session.subDependencies || []);
        
        const sessionComponents = sessionComponentIds
          .map((id: string) => allComponentsData.find((c: Component) => c.id === id))
          .filter((c): c is Component => c !== undefined);

        setFeatures(sessionFeatures);
        setComponents(sessionComponents);
        if (testPlanData) {
          setTestPlan(testPlanData);
        }
      } catch (error) {
        console.error('Error fetching session details:', error);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchSessionDetails();
  }, [session]);

  const getBugCount = (sess: Session): number => {
    // Only count linked bugs (bugUrls), not bugged sub-deps
    // This prevents double counting since bugs are linked to sub-deps
    return sess.bugUrls?.length || 0;
  };

  const getPRFeedbackCount = (sess: Session): number => {
    return sess.prFeedbacks?.length || 0;
  };

  const handleDeleteSession = async () => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await apiRequest(`/api/sessions/${session.id}`, { method: 'DELETE' }, { useAuth: true });
      if (onSessionChange) {
        await onSessionChange();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session');
    }
  };

  const handleReopenSession = async () => {
    if (!window.confirm('Are you sure you want to reopen this session? It will be moved to "In Progress" status.')) {
      return;
    }

    try {
      await apiRequest(`/api/sessions/${session.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'inprogress',
          completedAt: null
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      if (onSessionChange) {
        await onSessionChange();
      }
      onClose();
    } catch (error) {
      console.error('Error reopening session:', error);
      alert('Failed to reopen session');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: zIndex,
        padding: '2rem',
        overflow: 'auto'
      }}
    >
      <div 
        ref={detailsRef}
        className="card" 
        style={{ 
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          zIndex: zIndex + 1,
          position: 'relative',
          backgroundColor: theme.surface,
          border: `1px solid ${theme.gridLine}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Full-screen loading overlay */}
        {detailsLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            borderRadius: '8px'
          }}>
            <div style={{ 
              textAlign: 'center',
              color: theme.textSecondary,
              fontSize: '1rem'
            }}>
              Loading...
            </div>
          </div>
        )}

        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${theme.gridLine}`,
          opacity: detailsLoading ? 0 : 1,
          transition: 'opacity 0.2s'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: theme.textPrimary }}>
              Session Details
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {onBackToTestPlan && (
                <button
                  className="btn btn-secondary"
                  onClick={onBackToTestPlan}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  ← Back to Test Plan
                </button>
              )}
              {session.status === 'completed' && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={handleReopenSession}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                  >
                    Reopen
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleDeleteSession}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                  >
                    Delete
                  </button>
                </>
              )}
              {userRole === 'admin' && session.status === 'completed' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (onEdit) {
                      onEdit(session.id);
                    } else {
                      navigate(`/edit-session/${session.id}`);
                    }
                  }}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  Edit
                </button>
              )}
              <button
                className="btn btn-ghost btn-icon"
                onClick={onClose}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
          </div>
        </div>

      <div style={{ padding: '1.5rem', opacity: detailsLoading ? 0 : 1, transition: 'opacity 0.2s' }}>
        {/* Basic Information */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: `1px solid ${theme.gridLine}`
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Session ID
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
              {session.id}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Type
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
              {session.sessionType}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              User
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
              {session.user}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Platform
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
              {session.platforms?.length ? session.platforms.join(', ') : session.platform || 'N/A'}
            </div>
          </div>
          {session.milestone && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Milestone
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {session.milestone}
              </div>
              {milestones[session.milestone] && (
                <div style={{ fontSize: '0.75rem', color: theme.textSecondary, marginTop: '0.25rem' }}>
                  {(() => {
                    const milestoneMeta = milestones[session.milestone];
                    const startDate = formatDate(milestoneMeta.startDate, 'MMM dd, yyyy');
                    const endDate = milestoneMeta.endDate ? formatDate(milestoneMeta.endDate, 'MMM dd, yyyy') : null;
                    return endDate ? `${startDate} - ${endDate}` : `Started: ${startDate}`;
                  })()}
                </div>
              )}
            </div>
          )}
          {session.deadline && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Deadline
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {formatDate(session.deadline, 'MMM dd, yyyy')}
              </div>
            </div>
          )}
          {session.firstStartedAt && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Started
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {formatDate(session.firstStartedAt, 'MMM dd, yyyy h:mm a')}
              </div>
            </div>
          )}
          {session.completedAt && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Completed
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {formatDate(session.completedAt, 'MMM dd, yyyy h:mm a')}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Duration
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
              {formatDuration(session.duration)}
            </div>
          </div>
          {session.testPlanUrl && (() => {
            // Extract test plan ID from URL (e.g., "http://localhost:3000/testplan/123" -> "123")
            const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
            const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
            
            const handleOpenTestPlan = async () => {
              if (!testPlanId) return;
              
              try {
                // Fetch test plan and related data
                const [testPlanData, featuresData, componentsData] = await Promise.all([
                  apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true }),
                  apiRequest<Feature[]>('/api/features', {}, { useAuth: true }),
                  apiRequest<Component[]>('/api/components', {}, { useAuth: true })
                ]);
                
                setTestPlan(testPlanData);
                setTestPlanFeatures(Array.isArray(featuresData) ? featuresData : []);
                setTestPlanComponents(Array.isArray(componentsData) ? componentsData : []);
                setShowTestPlanModal(true);
              } catch (error) {
                console.error('Error fetching test plan:', error);
              }
            };
            
            return (
              <div>
                <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                  Test Plan
                </div>
                <div>
                  {testPlanId ? (
                    <button
                      type="button"
                      onClick={handleOpenTestPlan}
                      style={{ 
                        color: theme.cyanGlow, 
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {testPlan?.title || session.testPlanUrl}
                    </button>
                  ) : (
                    <a 
                      href={session.testPlanUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: theme.cyanGlow, 
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {session.testPlanUrl}
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
          {session.referenceUrl && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Reference
              </div>
              <div>
                <a 
                  href={session.referenceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.cyanGlow, 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    wordBreak: 'break-all'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {session.referenceUrl}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {session.notes && (
          <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '0.75rem'
            }}>
              Notes
            </h3>
            <div style={{ 
              padding: '1rem',
              backgroundColor: theme.surface,
              borderRadius: '6px',
              border: `1px solid ${theme.gridLine}`,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              color: theme.textPrimary
            }}>
              {session.notes}
            </div>
          </div>
        )}

        {/* Features */}
        {features.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: `1px solid ${theme.gridLine}`
            }}>
              Features
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {features.map((feature: Feature) => (
                <a
                  key={feature.id}
                  href={`#/feature/${feature.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/feature/${feature.id}`);
                  }}
                  className="badge badge-cyan"
                  style={{ 
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--border-glow)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {feature.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Components Status */}
        {components.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: `1px solid ${theme.gridLine}`
            }}>
              Components
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {components.map((component: Component) => {
                // Check componentStatuses first, fall back to subDepStatuses for legacy
                const componentStatus = session.componentStatuses?.find((s: ComponentStatus) => s.componentId === component.id);
                const legacyStatus = session.subDepStatuses?.find((s: SubDepStatus) => s.subDepId === component.id);
                const status = componentStatus || legacyStatus;
                const isTested = status?.tested || false;
                
                // Get bugs linked to this component (check both componentId and subDepId for legacy)
                const linkedBugs = (session.bugUrls || []).filter((bugUrl: BugUrl) => {
                  if (typeof bugUrl === 'string') return false;
                  return bugUrl.componentId === component.id || bugUrl.subDepId === component.id;
                }) as BugUrl[];
                
                // Get PR feedback linked to this component
                const componentFeedbacks = (session.prFeedbacks || []).filter((feedback: PRFeedback) => 
                  feedback.componentId === component.id
                );
                
                // Component is bugged if status says so OR if there are linked bugs
                const isBugged = status?.bugged || linkedBugs.length > 0;
                
                return (
                  <div
                    key={component.id}
                    style={{
                      padding: '1rem',
                      backgroundColor: isBugged ? theme.dangerTransparent : isTested ? theme.cyanTransparentLight : theme.surface,
                      border: `1px solid ${isBugged ? theme.danger : isTested ? theme.cyanDim : theme.gridLine}`,
                      borderRadius: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        <span style={{ 
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: isTested ? theme.success : theme.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {isTested ? 'Tested' : 'Not Tested'}
                        </span>
                      <span style={{ 
                        fontSize: '0.9rem',
                        fontWeight: isBugged ? '600' : '500',
                          color: isBugged ? theme.danger : theme.textPrimary
                      }}>
                        {component.name}
                      </span>
                      {isBugged && (
                        <span style={{ 
                            fontSize: '0.75rem',
                            fontWeight: '600',
                          color: theme.danger,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Bugged
                        </span>
                      )}
                      </div>
                    </div>
                    
                    {/* Show tested platforms with timestamps */}
                    {isTested && componentStatus?.testedPlatforms && componentStatus.testedPlatforms.length > 0 && (
                      <div style={{ 
                        marginTop: '0.5rem',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem'
                      }}>
                        {componentStatus.testedPlatforms.map((platform) => {
                          const testedAt = componentStatus.testedAt?.[platform];
                          return (
                            <div
                              key={platform}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: theme.surfaceElevated,
                                borderRadius: '4px',
                                border: `1px solid ${theme.gridLine}`,
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              <span style={{ fontWeight: '600', color: theme.textPrimary }}>
                                {platform}
                              </span>
                              {testedAt && (
                                <span style={{ color: theme.textMuted }}>
                                  {formatDate(testedAt, 'MMM dd h:mm a')}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Show linked bugs under the component */}
                    {linkedBugs.length > 0 && (
                      <div style={{ 
                        marginTop: '0.5rem',
                        paddingTop: '0.75rem',
                        borderTop: `1px solid ${theme.gridLine}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        {linkedBugs.map((bugUrl, bugIndex) => {
                          const bugFeature = features.find(f => f.id === bugUrl.featureId);
                          return (
                            <div 
                              key={bugIndex}
                              style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.surfaceElevated,
                                borderRadius: '4px',
                                border: `1px solid ${theme.gridLine}`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem'
                              }}
                            >
                              <a
                                href={bugUrl.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: theme.cyanGlow,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                                  wordBreak: 'break-all',
                                  fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                                  e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                                  e.currentTarget.style.textDecoration = 'none';
                                }}
                              >
                                {bugUrl.url}
                              </a>
                              <div style={{ 
                                fontSize: '0.75rem', 
                                color: theme.textMuted, 
                                display: 'flex', 
                                gap: '1rem', 
                                flexWrap: 'wrap',
                                marginTop: '0.25rem'
                              }}>
                                {bugFeature && (
                                  <span>Feature: <strong>{bugFeature.name}</strong></span>
                                )}
                                {bugUrl.platforms && bugUrl.platforms.length > 0 && (
                                  <span>Platforms: <strong>{bugUrl.platforms.join(', ')}</strong></span>
                                )}
                                {bugUrl.createdAt && (
                                  <span>Created: <strong>{formatDate(bugUrl.createdAt, 'MMM dd, yyyy h:mm a')}</strong></span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Show PR feedback under the component */}
                    {componentFeedbacks.length > 0 && (
                      <div style={{ 
                        marginTop: '0.5rem',
                        paddingTop: '0.75rem',
                        borderTop: `1px solid ${theme.gridLine}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: theme.textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '0.25rem'
                        }}>
                          PR Feedback ({componentFeedbacks.length})
                        </div>
                        {componentFeedbacks.map((feedback: PRFeedback) => {
                          const feedbackFeature = features.find(f => f.id === feedback.featureId);
                          const getSeverityColor = (severity: string) => {
                            switch (severity) {
                              case 'critical': return '#dc3545';
                              case 'high': return '#fd7e14';
                              case 'medium': return '#ffc107';
                              case 'low': return '#28a745';
                              default: return theme.textSecondary;
                            }
                          };
                          const getImpactLabel = (impact: string) => {
                            switch (impact) {
                              case 'production-blocker': return 'Production Blocker';
                              case 'user-facing': return 'User-Facing';
                              case 'internal': return 'Internal';
                              case 'minor-polish': return 'Minor Polish';
                              default: return impact;
                            }
                          };
                          const getCommentTypeLabel = (type: string) => {
                            switch (type) {
                              case 'bug': return 'Bug';
                              case 'design-feedback': return 'Design Feedback';
                              case 'edge-case': return 'Edge Case';
                              case 'usability-issue': return 'Usability Issue';
                              default: return type;
                            }
                          };
                          
                          return (
                            <div 
                              key={feedback.id}
                              style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.surfaceElevated,
                                borderRadius: '4px',
                                border: `1px solid ${theme.gridLine}`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <a
                                  href={feedback.prUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: theme.cyanGlow,
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                  }}
                                >
                                  PR Link
                                </a>
                                {feedback.commentUrl && (
                                  <a
                                    href={feedback.commentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: theme.cyanGlow,
                                      textDecoration: 'none',
                                      fontSize: '0.875rem',
                                      fontWeight: '500',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.textDecoration = 'none';
                                    }}
                                  >
                                    Comment Link
                                  </a>
                                )}
                                <span style={{ 
                                  fontSize: '0.75rem', 
                                  padding: '0.15rem 0.5rem', 
                                  borderRadius: '3px', 
                                  backgroundColor: getSeverityColor(feedback.severity), 
                                  color: 'white', 
                                  fontWeight: '600' 
                                }}>
                                  {feedback.severity.toUpperCase()}
                                </span>
                                <span style={{ 
                                  fontSize: '0.75rem', 
                                  color: theme.textMuted, 
                                  padding: '0.15rem 0.5rem', 
                                  backgroundColor: theme.surface, 
                                  borderRadius: '3px' 
                                }}>
                                  {getImpactLabel(feedback.impact)}
                                </span>
                                <span style={{ 
                                  fontSize: '0.75rem', 
                                  color: theme.textMuted 
                                }}>
                                  {getCommentTypeLabel(feedback.commentType)}
                                </span>
                              </div>
                              {feedback.description && (
                                <div style={{ 
                                  fontSize: '0.875rem', 
                                  color: theme.textPrimary, 
                                  whiteSpace: 'pre-wrap', 
                                  wordBreak: 'break-word' 
                                }}>
                                  {feedback.description}
                                </div>
                              )}
                              <div style={{ 
                                fontSize: '0.75rem', 
                                color: theme.textMuted, 
                                display: 'flex', 
                                gap: '1rem', 
                                flexWrap: 'wrap',
                                marginTop: '0.25rem'
                              }}>
                                {feedbackFeature && (
                                  <span>Feature: <strong>{feedbackFeature.name}</strong></span>
                                )}
                                <span>Created: <strong>{formatDate(feedback.createdAt, 'MMM dd, yyyy h:mm a')}</strong></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Summary */}
        <div style={{
          padding: '1rem',
          backgroundColor: theme.surface,
          borderRadius: '6px',
          border: `1px solid ${theme.gridLine}`
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600',
            color: theme.textPrimary,
            marginBottom: '0.75rem'
          }}>
            Summary
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>
                Bugs Found
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                color: getBugCount(session) > 0 ? '#e74c3c' : '#27ae60'
              }}>
                {getBugCount(session)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>
                Components Tested
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                color: theme.textPrimary
              }}>
                {(session.componentStatuses || []).filter((s: ComponentStatus) => s.tested).length} / {(session.components && session.components.length > 0) ? session.components.length : (session.subDependencies?.length || 0)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>
                PR Feedback
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                color: getPRFeedbackCount(session) > 0 ? theme.cyanGlow : theme.textMuted
              }}>
                {getPRFeedbackCount(session)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Test Plan Details Modal */}
      {showTestPlanModal && testPlan && (
        <TestPlanDetails
          scope={testPlan}
          onClose={() => setShowTestPlanModal(false)}
          features={testPlanFeatures}
          components={testPlanComponents}
          projectCode={projectCode}
          onEdit={() => {
            // Don't allow editing from session details - just close modal
            setShowTestPlanModal(false);
          }}
          onDelete={() => {
            // Don't allow deleting from session details - just close modal
            setShowTestPlanModal(false);
          }}
          onBack={() => {
            // Go back to session details
            setShowTestPlanModal(false);
          }}
          zIndex={2000}
        />
      )}
    </div>
  );
};

export default SessionDetails;
